/**
 * useGoals — tenant-wide goals query + mutations (Story 18.8, AC #2/#8/#10)
 *
 * - `useGoals()` reads GET /api/goals (the 18.8 list endpoint), unwrapping the
 *   { data: { goals } } envelope. Query key: ["goals"] (the stable contract
 *   the 18.9 cron and 18.10 plan-gating consumers depend on — do not rename).
 * - Pause/resume + delete mutations hit 18.3's PER-CLIENT endpoints
 *   (PATCH/DELETE /api/clients/:clientId/goals/:goalId — NO tenant-wide
 *   write endpoint exists). On success they invalidate BOTH ["goals"] (this
 *   page) AND ["client-goals", clientId] (the 18.3 client card) so the two
 *   surfaces never disagree (the TanStack v5 invalidate-both idiom).
 *
 * Pause = { status: "paused" }; resume = { status: "on_track" } — display
 * truth reverts to the freshly-derived status immediately, and the 18.9 cron
 * re-derives on its next tick (AC#10: resume never guesses the right status).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPatch, apiDelete } from "../lib/apiClient";
import type { GoalListItem } from "../lib/goalPageDisplay";

export const GOALS_QUERY_KEY = ["goals"] as const;

async function fetchGoals(): Promise<GoalListItem[]> {
  const res = await apiGet("/api/goals");
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail ?? err.title ?? "Failed to load goals");
  }
  const json = await res.json();
  return json.data.goals as GoalListItem[];
}

export function useGoals() {
  return useQuery<GoalListItem[]>({
    queryKey: GOALS_QUERY_KEY,
    queryFn: fetchGoals,
  });
}

/** Cross-invalidates both the tenant-wide list and the per-client card. */
function useGoalCrossInvalidate() {
  const queryClient = useQueryClient();
  return (clientId: string) => {
    queryClient.invalidateQueries({ queryKey: GOALS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ["client-goals", clientId] });
  };
}

export interface GoalStatusMutationVars {
  clientId: string;
  goalId: string;
  status: "paused" | "on_track" | "off_track" | "complete";
}

/** Pause / resume (and any direct status set) via the per-client PATCH. */
export function useSetGoalStatus() {
  const invalidate = useGoalCrossInvalidate();
  return useMutation({
    mutationFn: async ({ clientId, goalId, status }: GoalStatusMutationVars) => {
      const res = await apiPatch(
        `/api/clients/${clientId}/goals/${goalId}`,
        { status },
      );
      const json = await res
        .json()
        .catch(() => ({ detail: "Server error. Please try again." }));
      if (!res.ok) throw { status: res.status, ...json };
      return json.data;
    },
    onSuccess: (_data, vars) => invalidate(vars.clientId),
  });
}

export interface DeleteGoalVars {
  clientId: string;
  goalId: string;
}

export function useDeleteGoal() {
  const invalidate = useGoalCrossInvalidate();
  return useMutation({
    mutationFn: async ({ clientId, goalId }: DeleteGoalVars) => {
      const res = await apiDelete(`/api/clients/${clientId}/goals/${goalId}`);
      if (!res.ok && res.status !== 204) {
        const json = await res
          .json()
          .catch(() => ({ detail: "Server error. Please try again." }));
        throw { status: res.status, ...json };
      }
      return true;
    },
    onSuccess: (_data, vars) => invalidate(vars.clientId),
  });
}
